<script setup lang="ts">
import { ref, watch, computed } from "vue";
import type {
  IInputCodeProps,
  IInputCodeEmits,
  IInputCodeExpose,
} from "./VInputCode.types";

const props = withDefaults(defineProps<IInputCodeProps>(), {
  fieldsCount: 5,
});
const emit = defineEmits<IInputCodeEmits>();

defineOptions({ inheritAttrs: false });

const parseDigitsValue = (code: string): string[] => {
  if (code && !isNaN(Number(code)) && !isNaN(parseFloat(code))) {
    const splitted = code.split("");
    splitted.length = props.fieldsCount;
    return splitted.fill("", code.length);
  }
  return Array(props.fieldsCount).fill("");
};

const digitsValues = ref<string[]>(parseDigitsValue(props.modelValue));
const wrapper = ref<HTMLDivElement | null>(null);
const activeInput = ref(0);

watch(
  () => [...digitsValues.value],
  (nextDigit) => {
    const val = nextDigit.join("");
    emit("update:modelValue", val);
    if (val.length === props.fieldsCount) {
      emit("complete", val);
    }
  },
);

const focusInput = (input: number) => {
  activeInput.value = Math.max(
    Math.min(digitsValues.value.length - 1, input),
    0,
  );
};

const onKeyDown = (event: KeyboardEvent, index: number) => {
  const currentElement = event.target as HTMLInputElement;

  switch (event.key) {
    case "Backspace": {
      if (currentElement.value === "") {
        const prevElement =
          currentElement.previousElementSibling as HTMLInputElement;
        if (prevElement) {
          prevElement.focus();
        }
      }
      break;
    }
    case "ArrowLeft": {
      focusInput(activeInput.value - 1);
      event.preventDefault();
      break;
    }
    case "ArrowRight": {
      focusInput(activeInput.value + 1);
      event.preventDefault();
      break;
    }
    default: {
      // @NOTE: In Chrome input[type=number] misses the special symbols
      const isNumber = !isNaN(Number(event.key));
      const isPaste = event.metaKey && event.key === "v";
      if (!isPaste && (currentElement.value.length === 1 || !isNumber)) {
        event.preventDefault();
      }
      if (isNumber) {
        onInput(event, index, event.key);
      }
    }
  }
};

const onInput = (event: Event, index: number, newVal?: string) => {
  const currentElement = event.target as HTMLInputElement;
  let nextValue = "";

  if (currentElement.value !== "") {
    if (newVal) {
      currentElement.value = newVal;
    }

    nextValue =
      currentElement.value.length > 1
        ? currentElement.value[0]
        : currentElement.value;

    const nextElement = currentElement.nextElementSibling as HTMLInputElement;
    if (nextElement) {
      nextElement.focus();
    }
  }

  digitsValues.value.splice(index, 1, nextValue);
};

const onPaste = (event: ClipboardEvent) => {
  event.preventDefault();

  if (event.clipboardData) {
    const pastedData = event.clipboardData
      .getData("text/plain")
      .slice(0, digitsValues.value.length)
      .split("");
    if (!pastedData.join("").match(/^\d+$/)) {
      return;
    }

    // Вставка данных с учетом позиции курсора и длинны текущего значения
    const currentCharsInOtp = digitsValues.value.slice(
      0,
      Math.min(activeInput.value, digitsValues.value.join("").length),
    );
    const combinedWithPastedData = currentCharsInOtp.concat(pastedData);
    combinedWithPastedData
      .slice(0, digitsValues.value.length)
      .forEach((value, i) => {
        digitsValues.value[i] = value;
      });

    activeInput.value = combinedWithPastedData.slice(
      0,
      digitsValues.value.length - 1,
    ).length;
  }
};

const onFocus = (index: number) => {
  activeInput.value = index;
};
const onBlur = () => {
  activeInput.value = -1;
};

const clear = () => {
  digitsValues.value = [...Array(props.fieldsCount).fill("")];
};

const focus = () => {
  if (wrapper.value) {
    const firstInput = wrapper.value.children[0] as HTMLInputElement;
    firstInput.focus();
  }
};

watch(activeInput, () => {
  if (activeInput.value >= 0 && wrapper.value) {
    const input = wrapper.value.children[activeInput.value] as HTMLInputElement;
    input.focus();
  }
});

watch(
  () => props.modelValue,
  () => (digitsValues.value = parseDigitsValue(props.modelValue)),
);

const rootClass = computed(() => ({
  "input-code": true,
  "input-code_error": props.hasError,
  "input-code_success": props.success,
}));

defineExpose<IInputCodeExpose>({
  clear,
  focus,
});
</script>

<template>
  <div v-bind="$attrs" ref="wrapper" :class="rootClass">
    <input
      v-for="(value, index) in digitsValues"
      :key="`code-input-${index}`"
      :value="value"
      :disabled="disabled"
      :autofocus="!index"
      class="input-code__field"
      autocomplete="off"
      maxlength="1"
      min="0"
      max="9"
      step="1"
      type="number"
      inputmode="numeric"
      placeholder=" "
      @keydown="(event: KeyboardEvent) => onKeyDown(event, index)"
      @paste="onPaste"
      @focus="onFocus(index)"
      @blur="onBlur"
      @input="(event: Event) => onInput(event, index)"
    />
  </div>
</template>

<style lang="postcss" scoped>
.input-code {
  display: flex;
  justify-content: space-between;
  width: 100%;
  min-width: 296px;
  max-width: 360px;

  &__field {
    outline: none;
    font: var(--pl-font-headline-small);
    text-align: center;
    padding: 12px 16px;
    min-width: 0;
    background-color: var(--pl-field-background-default);
    border: none;
    border-radius: var(--pl-field-radius-m);

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      appearance: none !important;
      margin: 0;
    }

    &[type="number"] {
      appearance: textfield;
    }

    &::placeholder {
      color: var(--pl-field-fill-dots);
      background: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOCIgaGVpZ2h0PSI4IiB2aWV3Qm94PSIwIDAgOCA4IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxjaXJjbGUgY3g9IjQiIGN5PSI0IiByPSI0IiBmaWxsPSIjYzRjNGM0Ii8+PC9zdmc+")
        50% 50% no-repeat;
    }

    &:focus {
      background-color: var(--pl-field-background-focused);

      &::placeholder {
        background: none;
      }
    }

    &:active {
      background-color: var(--pl-field-background-pressed);
    }

    &:disabled {
      background-color: var(--pl-field-background-disabled);
      color: var(--pl-text-tertiary);
      cursor: not-allowed;
    }

    & + & {
      margin-left: 8px;
    }
  }

  &_error &__field {
    background-color: var(--pl-system-error-weak-default);
    color: var(--pl-text-system-error);
    animation: shake-animation 0.3s 1 linear;

    &::placeholder {
      color: var(--pl-text-system-error);
      background: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOCIgaGVpZ2h0PSI4IiB2aWV3Qm94PSIwIDAgOCA4IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxjaXJjbGUgY3g9IjQiIGN5PSI0IiByPSI0IiBmaWxsPSIjYWQwMDAwIi8+PC9zdmc+")
        50% 50% no-repeat;
    }
  }

  &_success &__field {
    color: var(--pl-text-system-success);
    background-color: var(--pl-system-success-weak-default);

    &::placeholder {
      color: var(--pl-text-system-success);
      background: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOCIgaGVpZ2h0PSI4IiB2aWV3Qm94PSIwIDAgOCA4IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxjaXJjbGUgY3g9IjQiIGN5PSI0IiByPSI0IiBmaWxsPSIjMmM3ZTM0Ii8+PC9zdmc+")
        50% 50% no-repeat;
    }
  }

  &_error &__field:focus {
    &::placeholder {
      background: none;
    }
  }

  &_success &__field:focus {
    &::placeholder {
      background: none;
    }
  }

  &_error &__field:disabled {
    color: var(--pl-text-system-error);
    background-color: var(--pl-system-error-weak-default);
  }

  &_success &__field:disabled {
    color: var(--pl-text-system-success);
    background-color: var(--pl-system-success-weak-default);
  }

  @keyframes shake-animation {
    0% {
      transform: translate(20px);
    }

    20% {
      transform: translate(-20px);
    }

    40% {
      transform: translate(10px);
    }

    60% {
      transform: translate(-10px);
    }

    80% {
      transform: translate(6px);
    }

    100% {
      transform: translate(0);
    }
  }
}
</style>
